/**
 * Single source of truth for legacy / moved / renamed URLs.
 *
 * Every entry here is treated as a permanent (301-equivalent) redirect by the
 * SPA router: search engines follow the client-side redirect, users never hit a
 * 404, and link equity consolidates onto the canonical URL.
 *
 * Rules:
 * - `from` must be a path that no longer renders its own page.
 * - `to` must be a canonical route that exists in App.tsx (optionally with #hash).
 * - Never delete an entry once shipped — old links live forever (PDFs, emails,
 *   social posts, backlinks). Retire an entry only if the target itself dies.
 * - Set `prefix: true` to also forward every nested path (`/old/x` -> `/new/x`).
 */
export type RedirectRule = {
  from: string;
  to: string;
  /** Forward nested paths too: /from/anything -> /to/anything */
  prefix?: boolean;
  /** Why this redirect exists — keeps the map auditable. */
  reason?: string;
};

export const REDIRECT_MAP: RedirectRule[] = [
  // Consultation / contact consolidation
  { from: "/strategy", to: "/inquire", reason: "Renamed strategy page to /inquire" },
  { from: "/contact", to: "/inquire", reason: "Contact folded into inquiry funnel" },
  { from: "/assessment", to: "/framework#assessment", reason: "Assessment now a section of /framework" },

  // Digital products — canonical slug is /ebooks
  { from: "/e-books", to: "/ebooks", reason: "Alias" },
  { from: "/ebook", to: "/ebooks", reason: "Alias" },
  { from: "/library", to: "/ebooks", reason: "Renamed library to ebooks" },
  { from: "/resources", to: "/ebooks", reason: "Resources merged into ebooks" },
  { from: "/store", to: "/ebooks", reason: "Alias" },
  { from: "/shop", to: "/ebooks", reason: "Alias" },
  { from: "/downloads", to: "/ebooks", reason: "Alias" },
  { from: "/digital-products", to: "/ebooks", reason: "Alias" },

  // Print titles
  { from: "/book-list", to: "/books", reason: "Alias" },
  { from: "/bookstore", to: "/books", reason: "Alias" },

  // Social proof
  { from: "/testimonials", to: "/reviews", reason: "Testimonials page renamed to /reviews" },
  { from: "/testimonial", to: "/testimonial-intake", reason: "Alias" },
  { from: "/testimonials/new", to: "/testimonial-intake", reason: "Moved intake form" },
  { from: "/share", to: "/share-your-story", reason: "Alias" },
  { from: "/stories/new", to: "/share-your-story", reason: "Moved story submission form" },

  // Masterclass
  { from: "/built-on-purpose", to: "/masterclass", reason: "Rebranded masterclass URL" },
  { from: "/built-on-purpose-masterclass", to: "/masterclass", reason: "Rebranded masterclass URL" },
  { from: "/webinar", to: "/masterclass", reason: "Legacy webinar link" },

  // Advocacy / FSAP — legacy paths printed in downloaded retainer PDFs, keep forever
  { from: "/advocacy/apply", to: "/advocacy/intake", reason: "Renamed apply -> intake" },
  { from: "/fsap", to: "/advocacy", prefix: true, reason: "FSAP section moved under /advocacy" },
  { from: "/full-scope-advocacy", to: "/advocacy", prefix: true, reason: "Long slug shortened" },

  // Offers & legal
  { from: "/pricing", to: "/invest", reason: "Pricing page renamed to /invest" },
  { from: "/playbook", to: "/free", reason: "Playbook moved into free resources" },
  { from: "/disclaimer", to: "/legal", reason: "Merged into /legal" },
  { from: "/terms", to: "/legal", reason: "Merged into /legal" },
  { from: "/privacy", to: "/legal", reason: "Merged into /legal" },

  // Auth
  { from: "/login", to: "/auth", reason: "Alias" },
  { from: "/signin", to: "/auth", reason: "Alias" },
  { from: "/sign-in", to: "/auth", reason: "Alias" },
  { from: "/register", to: "/auth", reason: "Alias" },
];

/** All legacy paths — used by the sitemap generator and link checker to exclude them. */
export const REDIRECT_SOURCES = REDIRECT_MAP.map((r) => r.from);

/**
 * Resolve a pathname against the map, preserving query string and hash so UTM
 * attribution and deep-link anchors survive the hop.
 * Returns null when the path is not a redirect source.
 */
export function resolveRedirect(
  pathname: string,
  search = "",
  hash = "",
): string | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;

  for (const rule of REDIRECT_MAP) {
    const isExact = path.toLowerCase() === rule.from.toLowerCase();
    const isNested =
      rule.prefix && path.toLowerCase().startsWith(`${rule.from.toLowerCase()}/`);
    if (!isExact && !isNested) continue;

    const suffix = isNested ? path.slice(rule.from.length) : "";
    const [targetPath, targetHash] = rule.to.split("#");
    const finalHash = targetHash ? `#${targetHash}` : hash;
    return `${targetPath}${suffix}${search}${finalHash}`;
  }
  return null;
}
